/**
 * Ajustes Component - MODULARIZED
 * 
 * Main settings configuration page with modular components
 * Improvements:
 * - Extracted custom hooks for data fetching and auto-save
 * - Separated UI into focused sub-components
 * - Removed gradients from orange backgrounds
 * - Removed borders from cards
 * - Cleaner, more maintainable architecture
 */

import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  addPaymentTypeToLocal,
  deletePaymentTypeFromLocal,
} from '../../../../../../crud/payments_api/payment_types';
import LoadingSpinner from '../../../../../../components/common/LoadingSpinner';
import { DEFAULT_CONFIG, getLocalId } from './utils';
import { useAjustesData, useAutoSave } from './hooks';
import MainSettingsSection from './components/MainSettingsSection';
import RegionalSettingsSection from './components/RegionalSettingsSection';
import PaymentTypesSection from './components/PaymentTypesSection';
import LogoUploadSection from './components/LogoUploadSection';
import ResetDefaultsButton from './components/ResetDefaultsButton';
import CustomPaymentsSection from './components/CustomPaymentsSection';

const Ajustes: React.FC = () => {
  // Data fetching hook
  const {
    isLoading,
    languages,
    timezones,
    monedas,
    cajas,
    paymentTypes,
    localPaymentTypes,
    config,
  } = useAjustesData();

  // Form state
  const [currentLogoUrl, setCurrentLogoUrl] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [imprimirOrden, setImprimirOrden] = useState<boolean>(false);
  const [idioma, setIdioma] = useState<string>('');
  const [zonaHoraria, setZonaHoraria] = useState<string>('');
  const [moneda, setMoneda] = useState<string>('');
  const [caja, setCaja] = useState<string>('');
  const [dafualtTip, setDafualtTip] = useState<string>('');
  const [selectedPaymentTypes, setSelectedPaymentTypes] = useState<Set<number>>(new Set());
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  // Photo editor state
  const [logoToEdit, setLogoToEdit] = useState<File | null>(null);
  const [showPhotoEditor, setShowPhotoEditor] = useState<boolean>(false);

  // Auto-save hook
  useAutoSave({
    idioma,
    zonaHoraria,
    moneda,
    caja,
    isOpen,
    imprimirOrden,
    dafualtTip,
    selectedPaymentTypes,
    initialLoad,
  });

  // Initialize form from fetched config
  useEffect(() => {
    if (config) {
      setIdioma(config.language_id?.toString() || '');
      setZonaHoraria(config.timezone_id?.toString() || '');
      setMoneda(config.moneda_id?.toString() || '');
      setIsOpen(config.local_open);
      setImprimirOrden(config.print_orders);
      setCaja(config.main_caja_id?.toString() || '');
      setDafualtTip(config.default_tip?.toString() || '');
      setCurrentLogoUrl(config.image_url || null);
      setSelectedPaymentTypes(new Set(localPaymentTypes));
      setInitialLoad(false);
    }
  }, [config, localPaymentTypes]);

  // Mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 900);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Payment type toggle handler
  const togglePaymentType = async (id: number) => {
    const localId = getLocalId();

    setSelectedPaymentTypes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
        deletePaymentTypeFromLocal(localId, id)
          .then(() => toast.success(`Tipo de pago eliminado: ${id}`))
          .catch(() => toast.error(`Error al eliminar el tipo de pago: ${id}`));
      } else {
        newSet.add(id);
        addPaymentTypeToLocal(localId, id)
          .then(() => toast.success(`Tipo de pago agregado: ${id}`))
          .catch(() => toast.error(`Error al agregar el tipo de pago: ${id}`));
      }
      return newSet;
    });
  };

  // Reset to defaults
  const handleResetDefaults = () => {
    setIdioma(DEFAULT_CONFIG.idioma);
    setZonaHoraria(DEFAULT_CONFIG.zonaHoraria);
    setMoneda(DEFAULT_CONFIG.moneda);
    setIsOpen(DEFAULT_CONFIG.isOpen);
    setImprimirOrden(DEFAULT_CONFIG.imprimirOrden);
    setCaja(DEFAULT_CONFIG.cajaPrincipal);
    setDafualtTip(DEFAULT_CONFIG.default_tip);
  };

  // Logo upload handler
  const handleLogoUpload = (file: File) => {
    setLogoToEdit(file);
    setShowPhotoEditor(true);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="loading-container">
        <LoadingSpinner text="Cargando información de ajustes..." transparent={true} />
      </div>
    );
  }

  return (
    <div>
      <div className="settings-contenedor" style={{ marginTop: isMobile ? '20px' : '0px' }}>
        {/* Main Settings */}
        <MainSettingsSection
          imprimirOrden={imprimirOrden}
          dafualtTip={dafualtTip}
          caja={caja}
          cajas={cajas}
          onImprimirOrdenChange={setImprimirOrden}
          onDefaultTipChange={setDafualtTip}
          onCajaChange={setCaja}
        />

        {/* Regional Settings */}
        <RegionalSettingsSection
          idioma={idioma}
          zonaHoraria={zonaHoraria}
          moneda={moneda}
          languages={languages}
          timezones={timezones}
          monedas={monedas}
          onIdiomaChange={setIdioma}
          onZonaHorariaChange={setZonaHoraria}
          onMonedaChange={setMoneda}
        />

        {/* Payment Types */}
        <PaymentTypesSection
          paymentTypes={paymentTypes}
          selectedPaymentTypes={selectedPaymentTypes}
          onTogglePaymentType={togglePaymentType}
        />

        {/* Custom Payment Types */}
        <CustomPaymentsSection />

        {/* Logo Upload */}
        <LogoUploadSection
          currentLogoUrl={currentLogoUrl}
          logoToEdit={logoToEdit}
          showPhotoEditor={showPhotoEditor}
          onRemoveLogo={() => setCurrentLogoUrl(null)}
          onLogoUpload={handleLogoUpload}
          onPhotoEditorClose={() => setShowPhotoEditor(false)}
          onPhotoEditorSave={() => setShowPhotoEditor(false)}
          onFinalImageSet={(editedImage: File) => {
            setCurrentLogoUrl(URL.createObjectURL(editedImage));
            setLogoToEdit(null);
          }}
          onLogoUpdated={(newLogoUrl: string) => {
            setCurrentLogoUrl(newLogoUrl);
          }}
        />
      </div>

      {/* Reset Defaults Button */}
      <ResetDefaultsButton onReset={handleResetDefaults} />
    </div>
  );
};

export default Ajustes;
